import { z } from 'zod';
import { paginatedResponseSchema } from './common';

// Knowledge Base Icon Types (using lucide-react icon names)
// Icons use PascalCase naming in lucide-react v0.263.1
export const knowledgeBaseIconSchema = z.enum([
  'Folder',
  'Crown',
  'Expand',
  'Triangle',
  'FlaskConical',
  'LayoutGrid',
  'Flag',
  'Diamond',
  'Glasses',
  'Box',
  'Clock',
  'Grid',
  'Sparkles',
  'Star',
]);
export type KnowledgeBaseIcon = z.infer<typeof knowledgeBaseIconSchema>;

// All available icons for knowledge base selection
export const KNOWLEDGE_BASE_ICONS: KnowledgeBaseIcon[] = [
  'Folder',
  'Crown',
  'Expand',
  'Triangle',
  'FlaskConical',
  'LayoutGrid',
  'Flag',
  'Diamond',
  'Glasses',
  'Box',
  'Clock',
  'Grid',
  'Sparkles',
  'Star',
];

// Knowledge Base Owner schema
export const knowledgeBaseOwnerSchema = z.object({
  id: z.number().nullable(),
  email: z.string().email().nullable(),
  full_name: z.string().nullable(),
});
export type KnowledgeBaseOwner = z.infer<typeof knowledgeBaseOwnerSchema>;

// Embedding Model schema
export const embeddingModelSchema = z.object({
  id: z.number().nullable(),
  name: z.string().nullable(),
  display_name: z.string().nullable(),
});
export type EmbeddingModel = z.infer<typeof embeddingModelSchema>;

// Knowledge Base Metadata schema
export const knowledgeBaseMetaDataSchema = z
  .object({
    icon: knowledgeBaseIconSchema.optional(),
  })
  .passthrough();
export type KnowledgeBaseMetaData = z.infer<typeof knowledgeBaseMetaDataSchema>;

// Knowledge Base schema
export const knowledgeBaseSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1, 'knowledgeBase.errors.nameRequired'),
  description: z.string().nullable(),
  owner_id: z.number(),
  owner: knowledgeBaseOwnerSchema,
  is_public: z.boolean(),
  is_active: z.boolean(),
  is_default: z.boolean(),
  embedding_model_id: z.number().nullable(),
  embedding_model: embeddingModelSchema,
  chunk_size: z.number().int().positive(),
  chunk_overlap: z.number().int().nonnegative(),
  document_count: z.number().int().nonnegative(),
  total_tokens: z.number().int().nonnegative(),
  meta_data: knowledgeBaseMetaDataSchema.nullable(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
});
export type KnowledgeBase = z.infer<typeof knowledgeBaseSchema>;

// Create Knowledge Base Request schema
export const createKnowledgeBaseRequestSchema = z.object({
  name: z
    .string()
    .min(1, 'knowledgeBase.errors.nameRequired')
    .max(100, 'knowledgeBase.errors.nameTooLong'),
  description: z.string().max(500, 'knowledgeBase.errors.descriptionTooLong').nullable().optional(),
  is_public: z.boolean().optional(),
  embedding_model_id: z.number().int().positive().optional().nullable(),
  chunk_size: z.number().int().positive().optional(),
  chunk_overlap: z.number().int().nonnegative().optional(),
  meta_data: knowledgeBaseMetaDataSchema.nullable().optional(),
});
export type CreateKnowledgeBaseRequest = z.infer<typeof createKnowledgeBaseRequestSchema>;

// Update Knowledge Base Request schema
export const updateKnowledgeBaseRequestSchema = z.object({
  name: z.string().min(1).max(100).optional().nullable(),
  description: z.string().max(500).optional().nullable(),
  is_public: z.boolean().optional().nullable(),
  embedding_model_id: z.number().int().positive().optional().nullable(),
  chunk_size: z.number().int().positive().optional().nullable(),
  chunk_overlap: z.number().int().nonnegative().optional().nullable(),
  meta_data: knowledgeBaseMetaDataSchema.nullable().optional(),
});
export type UpdateKnowledgeBaseRequest = z.infer<typeof updateKnowledgeBaseRequestSchema>;

// Knowledge Base List Response schema
export const knowledgeBaseListResponseSchema = z.object({
  items: z.array(knowledgeBaseSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  per_page: z.number().int().positive(),
  pages: z.number().int().nonnegative(),
});
export type KnowledgeBaseListResponse = z.infer<typeof knowledgeBaseListResponseSchema>;

// Knowledge Base List Query Parameters schema
export const knowledgeBaseListParamsSchema = z.object({
  skip: z.number().int().nonnegative().optional(),
  limit: z.number().int().positive().optional(),
  search: z.string().optional(),
  is_active: z.boolean().optional(),
  is_public: z.boolean().optional(),
  owner_id: z.number().int().positive().optional(),
  include_default: z.boolean().optional(),
});
export type KnowledgeBaseListParams = z.infer<typeof knowledgeBaseListParamsSchema>;

// File Status Types schema
export const knowledgeBaseFileStatusSchema = z.enum([
  'pending',
  'uploading',
  'processing',
  'completed',
  'error',
]);
export type KnowledgeBaseFileStatus = z.infer<typeof knowledgeBaseFileStatusSchema>;

// Knowledge Base File schema (simplified)
export const knowledgeBaseFileSchema = z.object({
  id: z.string(),
});
export type KnowledgeBaseFile = z.infer<typeof knowledgeBaseFileSchema>;

// File List Response schema
export const knowledgeBaseFileListResponseSchema = paginatedResponseSchema(knowledgeBaseFileSchema);
export type KnowledgeBaseFileListResponse = z.infer<typeof knowledgeBaseFileListResponseSchema>;

// File List Query Parameters schema
export const knowledgeBaseFileListParamsSchema = z.object({
  page: z.number().int().nonnegative().optional(),
  page_size: z.number().int().positive().optional(),
  status: knowledgeBaseFileStatusSchema.optional(),
});
export type KnowledgeBaseFileListParams = z.infer<typeof knowledgeBaseFileListParamsSchema>;

// Share Permission Types schema
export const sharePermissionSchema = z.enum(['read', 'edit']);
export type SharePermission = z.infer<typeof sharePermissionSchema>;

// User Search Response schema
export const userSearchResponseSchema = z.object({
  items: z.array(
    z.object({
      id: z.number(),
      email: z.string().email(),
      full_name: z.string(),
    })
  ),
  total: z.number().int().nonnegative(),
});
export type UserSearchResponse = z.infer<typeof userSearchResponseSchema>;

// Share Create Request schema
export const knowledgeBaseShareCreateSchema = z.object({
  shared_with_user_id: z.number().int().positive(),
  permission: sharePermissionSchema,
});
export type KnowledgeBaseShareCreate = z.infer<typeof knowledgeBaseShareCreateSchema>;

// Share Update Request schema
export const knowledgeBaseShareUpdateSchema = z.object({
  shared_with_user_id: z.number().int().positive(),
  permission: sharePermissionSchema,
});
export type KnowledgeBaseShareUpdate = z.infer<typeof knowledgeBaseShareUpdateSchema>;

// Knowledge Base Share schema
export const knowledgeBaseShareSchema = z.object({
  id: z.number(),
  knowledge_base_id: z.string().uuid(),
  shared_with_user_id: z.number(),
  shared_with_user: z.object({
    id: z.number(),
    email: z.string().email(),
    full_name: z.string(),
  }),
  permission: sharePermissionSchema,
  created_at: z.string(),
});
export type KnowledgeBaseShare = z.infer<typeof knowledgeBaseShareSchema>;

// Shares Response schema
export const sharesResponseSchema = z.object({
  items: z.array(knowledgeBaseShareSchema),
});
export type SharesResponse = z.infer<typeof sharesResponseSchema>;

// Additional types for compatibility (not Zod schemas)
export interface UserSearchUser {
  id: number;
  email: string;
  full_name: string;
}
